import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { db } from '@/firebase/firebase';
import { isFirebaseOfflineError } from '@/services/errorReportingService';

/**
 * Possible values for the onboarding status field.
 * - Step anchor_id (e.g., 'step_1', 'step_2'): User is on that step
 * - 'completed': User has finished onboarding (visited #completed anchor)
 * - null: Status not yet loaded, user document doesn't exist, or Firebase error occurred
 */
export type OnboardingStatusValue = string;

// Firestore field name for onboarding status (camelCase to match onboardingProgress convention)
const FIRESTORE_FIELD_NAME = 'onboardingStatus';

/**
 * Custom hook to track and update the user's onboarding status.
 *
 * This hook manages a separate `onboardingStatus` field on the user document
 * in Firebase Firestore to track whether a user has started or completed onboarding.
 *
 * @param userId - The Firebase user ID. If null/undefined, the hook is inactive.
 * @returns An object containing:
 *   - status: Current onboarding status (null if not set or on error)
 *   - loading: Whether the status is being loaded from Firebase
 *   - error: Firebase error if one occurred during loading
 *   - setOnboardingStatus: Function to update the status
 *
 * @example
 * ```tsx
 * const { status, loading, error, setOnboardingStatus } = useOnboardingStatus(userId);
 *
 * // Update status when step changes
 * useEffect(() => {
 *   if (loading || !userId || status === 'completed') return;
 *   const anchorId = steps[currentStepIndex]?.anchorId;
 *   if (anchorId) {
 *     setOnboardingStatus(anchorId);
 *   }
 * }, [currentStepIndex, loading, status, setOnboardingStatus, userId, steps]);
 *
 * // Set to 'completed' when reaching the #completed anchor
 * if (isCompletedAnchor) {
 *   setOnboardingStatus('completed');
 * }
 * ```
 */
export const useOnboardingStatus = (userId?: string | null) => {
  const [status, setStatus] = useState<OnboardingStatusValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const statusRef = useRef<OnboardingStatusValue | null>(null);

  useEffect(() => {
    if (!userId) {
      setStatus(null);
      statusRef.current = null;
      setError(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);
    const docRef = doc(collection(db, 'users'), userId);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const rawStatus = data?.[FIRESTORE_FIELD_NAME];
          // Accept any string value (anchor_id like 'step_1', 'step_2', or 'completed')
          const validStatus = typeof rawStatus === 'string' ? rawStatus : null;

          setStatus(validStatus);
          statusRef.current = validStatus;
        } else {
          setStatus(null);
          statusRef.current = null;
        }
        setLoading(false);
      },
      (err) => {
        if (!isFirebaseOfflineError(err)) {
          Sentry.captureException(err, {
            tags: { feature: 'onboarding' },
            extra: { context: 'loading onboarding status', userId },
          });
        }
        const errorInstance = err instanceof Error ? err : new Error(String(err));
        setError(errorInstance);
        setStatus(null);
        statusRef.current = null;
        setLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [userId]);

  /**
   * Updates the onboarding status in the user's Firebase document.
   *
   * @param newStatus - The new status value (step anchor_id like 'step_1' or 'completed')
   * @returns Promise<boolean> - True if the operation completed successfully
   *   (including when no update was needed). Returns false on Firebase write failure or missing userId.
   */
  const setOnboardingStatus = useCallback(
    async (newStatus: OnboardingStatusValue): Promise<boolean> => {
      if (!userId) {
        return false;
      }

      // Skip update if status is already the same
      if (statusRef.current === newStatus) {
        return true;
      }

      // Store previous state for rollback on failure
      const previousStatus = statusRef.current;

      // Optimistically update local state
      statusRef.current = newStatus;
      setStatus(newStatus);

      try {
        const docRef = doc(collection(db, 'users'), userId);
        await setDoc(
          docRef,
          {
            [FIRESTORE_FIELD_NAME]: newStatus,
          },
          { merge: true },
        );
        return true;
      } catch (error) {
        // Revert optimistic update on failure
        statusRef.current = previousStatus;
        setStatus(previousStatus);

        if (!isFirebaseOfflineError(error)) {
          Sentry.captureException(error, {
            tags: { feature: 'onboarding' },
            extra: { context: 'updating onboarding status', userId, newStatus },
          });
        }
        return false;
      }
    },
    [userId],
  );

  return { status, loading, error, setOnboardingStatus };
};
